/**
 * <s-price-history> — PriceCharting price history line chart for the product page.
 *
 * Reads the product's custom.price_history JSON (USD cents per month) from a child
 * <script type="application/json" data-price-history>, converts each point to the
 * shop currency with the stored rate, and draws an SVG line chart with
 * 6m / 1y / All range buttons and a hover/keyboard tooltip.
 *
 * Required markup:
 *   script[data-price-history]  — JSON: { rate, points: [[timestamp_ms, usd_cents], ...] }
 *   [data-chart]                — element the SVG is drawn into
 *   button[data-range]          — range buttons; value is months ("6", "12") or "all"
 *   [data-tooltip]              — tooltip element (hidden until a point is active)
 *   [data-empty]                — message shown when the range has fewer than 2 points
 * Attributes:
 *   data-currency, data-locale, data-label (aria label prefix for the chart)
 */
class SPriceHistory extends HTMLElement {
  #ac;
  #points = [];
  #visible = [];
  #active = -1;
  #range = 'all';

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const source = this.querySelector('script[data-price-history]');
    if (!source) return;
    let data;
    try {
      data = JSON.parse(source.textContent);
    } catch {
      return;
    }
    const rate = Number(data.rate) || 1;
    this.#points = (data.points || [])
      .filter((p) => Array.isArray(p) && p[1] > 0)
      .map(([t, cents]) => ({ t: Number(t), v: Math.round(((cents / 100) * rate) / 50) * 50 }))
      .sort((a, b) => a.t - b.t);

    this.format = new Intl.NumberFormat(this.dataset.locale || undefined, {
      style: 'currency',
      currency: this.dataset.currency || 'PKR',
      maximumFractionDigits: 0,
    });
    this.dateFormat = new Intl.DateTimeFormat(this.dataset.locale || undefined, { month: 'short', year: 'numeric' });

    this.addEventListener('click', this.#handleRange, opts);
    this.chart = this.querySelector('[data-chart]');
    this.tooltip = this.querySelector('[data-tooltip]');
    this.empty = this.querySelector('[data-empty]');
    this.chart?.addEventListener('pointermove', this.#handlePointer, opts);
    this.chart?.addEventListener('pointerleave', this.#clearActive, opts);
    this.chart?.addEventListener('keydown', this.#handleKey, opts);
    this.chart?.addEventListener('blur', this.#clearActive, opts);

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #handleRange = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-range]');
    if (!button || !this.contains(button)) return;
    this.#range = button.dataset.range;
    for (const b of this.querySelectorAll('button[data-range]')) {
      b.setAttribute('aria-pressed', String(b === button));
    }
    this.#render();
  };

  #handlePointer = (event) => {
    if (this.#visible.length < 2 || !this.svg) return;
    const box = this.svg.getBoundingClientRect();
    const x = ((event.clientX - box.left) / box.width) * this.width;
    let nearest = 0;
    let best = Infinity;
    this.#visible.forEach((p, i) => {
      const d = Math.abs(this.#x(p.t) - x);
      if (d < best) {
        best = d;
        nearest = i;
      }
    });
    this.#setActive(nearest);
  };

  #handleKey = (event) => {
    if (this.#visible.length < 2) return;
    let i = this.#active < 0 ? this.#visible.length - 1 : this.#active;
    if (event.key === 'ArrowLeft') i = Math.max(0, i - 1);
    else if (event.key === 'ArrowRight') i = Math.min(this.#visible.length - 1, i + 1);
    else if (event.key === 'Home') i = 0;
    else if (event.key === 'End') i = this.#visible.length - 1;
    else return;
    event.preventDefault();
    this.#setActive(i);
  };

  #clearActive = () => {
    this.#active = -1;
    if (this.tooltip) this.tooltip.hidden = true;
    this.marker?.setAttribute('visibility', 'hidden');
    this.guide?.setAttribute('visibility', 'hidden');
  };

  #setActive(i) {
    const p = this.#visible[i];
    if (!p) return;
    this.#active = i;
    const cx = this.#x(p.t);
    const cy = this.#y(p.v);
    this.marker?.setAttribute('cx', cx);
    this.marker?.setAttribute('cy', cy);
    this.marker?.setAttribute('visibility', 'visible');
    this.guide?.setAttribute('x1', cx);
    this.guide?.setAttribute('x2', cx);
    this.guide?.setAttribute('visibility', 'visible');
    if (this.tooltip) {
      this.tooltip.textContent = `${this.dateFormat.format(p.t)}: ${this.format.format(p.v)}`;
      this.tooltip.hidden = false;
      const pct = (cx / this.width) * 100;
      this.tooltip.style.insetInlineStart = `${Math.min(Math.max(pct, 12), 88)}%`;
    }
  }

  #x(t) {
    const pad = 8;
    const [min, max] = this.tRange;
    if (max === min) return this.width / 2;
    return pad + ((t - min) / (max - min)) * (this.width - pad * 2 - this.axis);
  }

  #y(v) {
    const top = 12;
    const bottom = this.height - 24;
    const [min, max] = this.vRange;
    if (max === min) return (top + bottom) / 2;
    return bottom - ((v - min) / (max - min)) * (bottom - top);
  }

  #render() {
    if (!this.chart) return;
    this.#clearActive();
    let pts = this.#points;
    if (this.#range !== 'all' && pts.length) {
      const months = Number(this.#range);
      const cutoff = new Date(pts[pts.length - 1].t);
      cutoff.setMonth(cutoff.getMonth() - months);
      pts = pts.filter((p) => p.t >= cutoff.getTime());
    }
    this.#visible = pts;

    if (pts.length < 2) {
      this.chart.replaceChildren();
      if (this.empty) this.empty.hidden = false;
      return;
    }
    if (this.empty) this.empty.hidden = true;

    this.width = 600;
    this.height = 220;
    this.axis = 56;
    const values = pts.map((p) => p.v);
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    const spread = hi - lo || hi * 0.2 || 100;
    this.vRange = [Math.max(0, lo - spread * 0.15), hi + spread * 0.15];
    this.tRange = [pts[0].t, pts[pts.length - 1].t];

    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', `0 0 ${this.width} ${this.height}`);
    svg.setAttribute('class', 'block h-auto w-full overflow-visible');
    svg.setAttribute('aria-hidden', 'true');

    const gridX2 = this.width - this.axis;
    for (let i = 0; i <= 3; i++) {
      const v = this.vRange[0] + ((this.vRange[1] - this.vRange[0]) * i) / 3;
      const y = this.#y(v);
      const line = document.createElementNS(ns, 'line');
      line.setAttribute('x1', 0);
      line.setAttribute('x2', gridX2);
      line.setAttribute('y1', y);
      line.setAttribute('y2', y);
      line.setAttribute('stroke', 'currentColor');
      line.setAttribute('stroke-opacity', '0.12');
      svg.append(line);
      const label = document.createElementNS(ns, 'text');
      label.setAttribute('x', this.width);
      label.setAttribute('y', y + 4);
      label.setAttribute('text-anchor', 'end');
      label.setAttribute('font-size', '11');
      label.setAttribute('fill', 'currentColor');
      label.setAttribute('fill-opacity', '0.7');
      label.textContent = this.#short(v);
      svg.append(label);
    }

    const years = new Set();
    for (const p of pts) years.add(new Date(p.t).getFullYear());
    const yearList = [...years];
    const labelEvery = Math.ceil(yearList.length / 5);
    yearList.forEach((year, idx) => {
      const t = new Date(year, 0, 1).getTime();
      if (t < this.tRange[0] || idx % labelEvery) return;
      const label = document.createElementNS(ns, 'text');
      label.setAttribute('x', this.#x(t));
      label.setAttribute('y', this.height - 4);
      label.setAttribute('text-anchor', 'middle');
      label.setAttribute('font-size', '11');
      label.setAttribute('fill', 'currentColor');
      label.setAttribute('fill-opacity', '0.7');
      label.textContent = String(year);
      svg.append(label);
    });
    if (yearList.length === 1 || pts.length <= 6) {
      [pts[0], pts[pts.length - 1]].forEach((p, i) => {
        const label = document.createElementNS(ns, 'text');
        label.setAttribute('x', this.#x(p.t));
        label.setAttribute('y', this.height - 4);
        label.setAttribute('text-anchor', i ? 'end' : 'start');
        label.setAttribute('font-size', '11');
        label.setAttribute('fill', 'currentColor');
        label.setAttribute('fill-opacity', '0.7');
        label.textContent = this.dateFormat.format(p.t);
        svg.append(label);
      });
    }

    const d = pts.map((p, i) => `${i ? 'L' : 'M'}${this.#x(p.t).toFixed(1)} ${this.#y(p.v).toFixed(1)}`).join(' ');
    const area = document.createElementNS(ns, 'path');
    area.setAttribute('d', `${d} L${this.#x(pts[pts.length - 1].t).toFixed(1)} ${this.height - 24} L${this.#x(pts[0].t).toFixed(1)} ${this.height - 24} Z`);
    area.setAttribute('fill', 'var(--color-success)');
    area.setAttribute('fill-opacity', '0.1');
    svg.append(area);

    const path = document.createElementNS(ns, 'path');
    path.setAttribute('d', d);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'var(--color-success)');
    path.setAttribute('stroke-width', '2.5');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-linecap', 'round');
    svg.append(path);

    this.guide = document.createElementNS(ns, 'line');
    this.guide.setAttribute('y1', 12);
    this.guide.setAttribute('y2', this.height - 24);
    this.guide.setAttribute('stroke', 'currentColor');
    this.guide.setAttribute('stroke-opacity', '0.3');
    this.guide.setAttribute('stroke-dasharray', '3 3');
    this.guide.setAttribute('visibility', 'hidden');
    svg.append(this.guide);

    this.marker = document.createElementNS(ns, 'circle');
    this.marker.setAttribute('r', 5);
    this.marker.setAttribute('fill', 'var(--color-success)');
    this.marker.setAttribute('stroke', 'var(--color-surface)');
    this.marker.setAttribute('stroke-width', '2');
    this.marker.setAttribute('visibility', 'hidden');
    svg.append(this.marker);

    this.svg = svg;
    this.chart.replaceChildren(svg);

    const first = pts[0];
    const last = pts[pts.length - 1];
    const prefix = this.dataset.label || '';
    this.chart.setAttribute(
      'aria-label',
      `${prefix} ${this.dateFormat.format(first.t)} ${this.format.format(first.v)} – ${this.dateFormat.format(last.t)} ${this.format.format(last.v)}`.trim()
    );
  }

  #short(v) {
    if (v >= 100000) return `${(v / 1000).toFixed(0)}k`;
    if (v >= 10000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return Math.round(v).toLocaleString(this.dataset.locale || undefined);
  }
}

if (!customElements.get('s-price-history')) {
  customElements.define('s-price-history', SPriceHistory);
}
